import React, { useState, useEffect, useCallback } from "react"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import Input from "aldoo-ra/Input"
import DropdownSelect from "aldoo-ra/DropdownSelect"
import { showNotification } from "aldoo-ra/Notification"
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ArrowPathIcon,
} from "@heroicons/react/24/outline"
import Modal from "aldoo-ra/Modal"
import { GetParam, GetParams, CreateParam, UpdateParam } from "./api"
import ParamEditDialog from "./components/ParamEditDialog"

const SITE_ENVIRONMENTS_KEY = "SITE_ENVIRONMENTS"
const ALL_PARAMS_LIMIT = 10000

// ─── Remote helpers ────────────────────────────────────────────────────────────

async function fetchRemoteParams(env) {
  const base = env.api.replace(/\/$/, "")
  const res = await fetch(`${base}/v1/param-io`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "aldoo-api-auth-key": env.api_key,
    },
    body: JSON.stringify({ get_params: { itemsPerPage: ALL_PARAMS_LIMIT, page: 1 } }),
    signal: AbortSignal.timeout(15000),
  })
  const data = await res.json()
  return data.result ? data.data : []
}

async function writeRemoteParam(env, action, payload) {
  const base = env.api.replace(/\/$/, "")
  const res = await fetch(`${base}/v1/param-io`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "aldoo-api-auth-key": env.api_key,
    },
    body: JSON.stringify({ [action]: payload }),
    signal: AbortSignal.timeout(10000),
  })
  return await res.json()
}

// ─── Diff helpers ──────────────────────────────────────────────────────────────

function buildDiffMap(localParams, remoteParams) {
  const localMap = Object.fromEntries(localParams.map((p) => [p.address, p]))
  const remoteMap = Object.fromEntries(remoteParams.map((p) => [p.address, p]))
  const allAddresses = [...new Set([...Object.keys(localMap), ...Object.keys(remoteMap)])].sort()

  return allAddresses.map((address) => {
    const local = localMap[address] ?? null
    const remote = remoteMap[address] ?? null

    let status
    if (local && remote) {
      status = JSON.stringify(local.value) === JSON.stringify(remote.value) ? "same" : "diff"
    } else if (local) {
      status = "local-only"
    } else {
      status = "remote-only"
    }

    return { address, local, remote, status }
  })
}

function formatValue(value) {
  if (value === null || value === undefined) return "—"
  if (typeof value === "string") return value
  return JSON.stringify(value)
}

// ─── Stats bar ─────────────────────────────────────────────────────────────────

function StatsBar({ diffRows, pending, filter, setFilter, onCommit, committing }) {
  const pendingCount = Object.keys(pending).length
  const same = diffRows.filter((r) => r.status === "same").length
  const diff = diffRows.filter((r) => r.status === "diff").length
  const localOnly = diffRows.filter((r) => r.status === "local-only").length
  const remoteOnly = diffRows.filter((r) => r.status === "remote-only").length

  const toggle = (value) => setFilter((prev) => (prev === value ? "all" : value))

  const CHIP_STYLES = {
    all:          { active: "bg-zinc-400 text-black",    inactive: "text-zinc-400" },
    same:         { active: "bg-green-500 text-white",   inactive: "text-green-500" },
    diff:         { active: "bg-yellow-400 text-black",  inactive: "text-yellow-400" },
    "local-only": { active: "bg-blue-500 text-white",    inactive: "text-blue-400" },
    "remote-only":{ active: "bg-purple-500 text-white",  inactive: "text-purple-400" },
    pending:      { active: "bg-orange-500 text-black",  inactive: "text-orange-400" },
  }

  const chip = (value, count, label) => {
    const active = filter === value
    const styles = CHIP_STYLES[value] || CHIP_STYLES.all
    return (
      <button
        key={value}
        onClick={() => toggle(value)}
        className={`text-xs px-2.5 py-1 rounded-full font-medium transition-colors ${
          active ? styles.active : styles.inactive
        } hover:brightness-110`}
      >
        {count} {label}
      </button>
    )
  }

  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      {chip("all", diffRows.length, "total")}
      {chip("same", same, "same")}
      {diff > 0 && chip("diff", diff, "different")}
      {localOnly > 0 && chip("local-only", localOnly, "local-only")}
      {remoteOnly > 0 && chip("remote-only", remoteOnly, "remote-only")}
      {pendingCount > 0 && (
        <>
          {chip("pending", pendingCount, "pending")}
          <button
            onClick={onCommit}
            disabled={committing}
            className="text-xs px-2.5 py-1 rounded-full font-medium bg-admin_primary text-white disabled:opacity-50 transition-colors hover:brightness-110 ml-1"
          >
            {committing ? "Committing…" : "Commit"}
          </button>
        </>
      )}
    </div>
  )
}

// ─── Remote view dialog ────────────────────────────────────────────────────────

function RemoteParamView({ param, envName, onClose }) {
  if (!param) return null
  const value = typeof param.value === "object"
    ? JSON.stringify(param.value, null, 2)
    : String(param.value ?? "")

  return (
    <Modal variant="admin_compact_fit" title={param.address} isOpen onClose={onClose}>
      <div className="space-y-3 px-4 pb-4 w-[480px]">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs px-2 py-0.5 rounded-full bg-purple-500/15 text-purple-300 border border-purple-500/30">
            {envName}
          </span>
          {param.author && (
            <span className="text-xs text-admin_text_secondary">{param.author}</span>
          )}
        </div>

        {param.description && (
          <Typography variant="admin_txt2_dim">{param.description}</Typography>
        )}

        <pre className="text-xs font-mono bg-admin_canvas border border-admin_bordered rounded p-3 overflow-auto max-h-64 text-admin_text whitespace-pre-wrap break-all">
          {value}
        </pre>
      </div>
    </Modal>
  )
}

// ─── Row ───────────────────────────────────────────────────────────────────────

function DiffRow({ row, pending, onCopyToRemote, onCopyToLocal, onEditParam, onViewRemote }) {
  const { address, local, remote, status } = row

  const pendingEntry = pending[address]
  const hasPending = !!pendingEntry

  const rowBg =
    hasPending
      ? "bg-admin_primary/10 border-l-2 border-admin_primary"
      : status === "diff"
      ? "bg-yellow-500/5 border-l-2 border-yellow-400/50"
      : status === "local-only"
      ? "bg-blue-500/5 border-l-2 border-blue-400/50"
      : status === "remote-only"
      ? "bg-purple-500/5 border-l-2 border-purple-400/50"
      : ""

  const effectiveLocalValue = hasPending && pendingEntry.side === "local"
    ? pendingEntry.value
    : local?.value

  const effectiveRemoteValue = hasPending && pendingEntry.side === "remote"
    ? pendingEntry.value
    : remote?.value

  const pendingToRemote = hasPending && pendingEntry.side === "remote"
  const pendingToLocal  = hasPending && pendingEntry.side === "local"

  const chevronBtn = (onClick, disabled, active, title, children) => (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`p-1 rounded transition-colors shrink-0 ${
        active
          ? "text-admin_primary bg-admin_primary/20"
          : "text-admin_text_secondary/40 hover:text-admin_primary hover:bg-admin_primary/10 disabled:opacity-20 disabled:cursor-not-allowed"
      }`}
    >
      {children}
    </button>
  )

  return (
    <div className={`grid grid-cols-[30%_1fr_1fr] gap-2 px-3 py-2 rounded-md text-sm cursor-default group hover:bg-admin_primary/5 transition-colors ${rowBg}`}>
      {/* Address */}
      <div className="min-w-0">
        <span className="font-mono text-admin_text text-xs truncate block">{address}</span>
      </div>

      {/* Local value + → button */}
      <div className="flex items-center gap-2 min-w-0">
        <span
          className={`flex-1 font-mono text-xs truncate min-w-0 ${local ? "text-admin_text_secondary hover:text-admin_primary cursor-pointer transition-colors" : "text-admin_text_secondary/40 italic"}`}
          onClick={local ? () => onEditParam(local) : undefined}
          title={local ? "Click to edit" : undefined}
        >
          {local ? formatValue(effectiveLocalValue) : "not in local"}
        </span>
        {status !== "same" && chevronBtn(() => onCopyToRemote(row), !local, pendingToRemote, "Copy local → remote",
          <ChevronRightIcon className="w-5 h-5" />
        )}
      </div>

      {/* ← button + Remote value */}
      <div className="flex items-center gap-2 min-w-0">
        {status !== "same" && chevronBtn(() => onCopyToLocal(row), !remote, pendingToLocal, "Copy remote → local",
          <ChevronLeftIcon className="w-5 h-5" />
        )}
        <span
          className={`flex-1 font-mono text-xs truncate min-w-0 ${remote ? "text-admin_text_secondary hover:text-admin_primary cursor-pointer transition-colors" : "text-admin_text_secondary/40 italic"}`}
          onClick={remote ? () => onViewRemote(remote) : undefined}
          title={remote ? "Click to view" : undefined}
        >
          {remote ? formatValue(effectiveRemoteValue) : "not in remote"}
        </span>
      </div>
    </div>
  )
}

// ─── Main ──────────────────────────────────────────────────────────────────────


export default function ParamSync() {
  const [environments, setEnvironments] = useState([])
  const [selectedEnv, setSelectedEnv] = useState(null)
  const [localParams, setLocalParams] = useState([])
  const [remoteParams, setRemoteParams] = useState([])
  const [loading, setLoading] = useState(false)
  const [filter, setFilter] = useState("diff")
  const [search, setSearch] = useState("")
  const [pending, setPending] = useState({})
  const [committing, setCommitting] = useState(false)
  const [editingParam, setEditingParam] = useState(null)
  const [viewingRemote, setViewingRemote] = useState(null)

  // Load environments
  useEffect(() => {
    GetParam(SITE_ENVIRONMENTS_KEY).then((res) => {
      if (res?.value && Array.isArray(res.value)) setEnvironments(res.value)
    })
  }, [])

  // Load local params
  useEffect(() => {
    GetParams({ itemsPerPage: ALL_PARAMS_LIMIT, page: 1 }).then((res) => {
      if (res?.result) setLocalParams(res.data || [])
    })
  }, [])

  // Load remote params when env changes
  useEffect(() => {
    if (!selectedEnv) return
    setLoading(true)
    fetchRemoteParams(selectedEnv)
      .then((data) => setRemoteParams(data))
      .catch(() => {
        showNotification({
          message: "Failed to fetch remote params",
          className: "bg-admin_error text-white",
        })
        setRemoteParams([])
      })
      .finally(() => setLoading(false))
  }, [selectedEnv])

  const diffRows = buildDiffMap(localParams, remoteParams)

  const visibleRows = diffRows.filter((row) => {
    if (filter === "pending") {
      if (!pending[row.address]) return false
    } else if (filter !== "all" && row.status !== filter) {
      return false
    }
    if (search) {
      const q = search.toLowerCase()
      if (!row.address.toLowerCase().includes(q)) return false
    }
    return true
  })

  const pendingCount = Object.keys(pending).length

  const copyToRemote = useCallback((row) => {
    if (!row.local) return
    setPending((prev) => {
      const next = { ...prev }
      if (next[row.address]?.side === "remote") { delete next[row.address]; return next }
      next[row.address] = { side: "remote", action: row.remote ? "update_param" : "create_param", address: row.address, value: row.local.value, description: row.local.description }
      return next
    })
  }, [])

  const copyToLocal = useCallback((row) => {
    if (!row.remote) return
    setPending((prev) => {
      const next = { ...prev }
      if (next[row.address]?.side === "local") { delete next[row.address]; return next }
      next[row.address] = { side: "local", action: row.local ? "update_param" : "create_param", address: row.address, value: row.remote.value, description: row.remote.description }
      return next
    })
  }, [])

  const handleCommit = async () => {
    if (!pendingCount) return
    setCommitting(true)
    const entries = Object.values(pending)
    const errors = []

    for (const entry of entries) {
      try {
        const payload = { address: entry.address, value: entry.value, description: entry.description, author: "sync" }
        if (entry.side === "remote") {
          const res = await writeRemoteParam(selectedEnv, entry.action, payload)
          if (!res.result) errors.push(entry.address)
        } else {
          const fn = entry.action === "create_param" ? CreateParam : UpdateParam
          const res = await fn(payload)
          if (!res.result) errors.push(entry.address)
        }
      } catch {
        errors.push(entry.address)
      }
    }

    if (errors.length) {
      showNotification({
        message: `${errors.length} param(s) failed to sync`,
        className: "bg-admin_error text-white",
      })
    } else {
      showNotification({
        message: `${entries.length} param(s) synced`,
        className: "bg-admin_success text-white",
      })
    }

    setPending({})
    setCommitting(false)

    // Refresh both sides
    const [localRes, remoteData] = await Promise.all([
      GetParams({ itemsPerPage: ALL_PARAMS_LIMIT, page: 1 }),
      selectedEnv ? fetchRemoteParams(selectedEnv).catch(() => remoteParams) : Promise.resolve(remoteParams),
    ])
    if (localRes?.result) setLocalParams(localRes.data || [])
    setRemoteParams(remoteData)
  }

  const envOptions = environments.map((e) => ({ value: e.id, label: e.name }))
  const selectedEnvOption = envOptions.filter((o) => o.value === selectedEnv?.id)

  return (
    <div className="flex flex-col bg-admin_canvas overflow-hidden" style={{ height: "100vh" }}>
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-admin_bordered bg-admin_paper shrink-0">
        <div>
          <Typography component="h1" variant="admin_h3">
            Param Sync
          </Typography>
          <Typography variant="admin_txt1_dim">
            Compare and sync parameters across environments
          </Typography>
        </div>

        <div className="flex items-center gap-3">
          {selectedEnv && (
            <div className="w-56">
              <DropdownSelect
                variant="admin"
                options={envOptions}
                value={selectedEnvOption}
                onChange={(sel) => {
                  const env = environments.find((e) => e.id === sel[0]?.value)
                  setSelectedEnv(env ?? null)
                  setPending({})
                  setRemoteParams([])
                }}
                placeholder="Select remote env"
              />
            </div>
          )}

          {selectedEnv && (
            <Button
              variant="admin_icon"
              onClick={() => {
                setLoading(true)
                fetchRemoteParams(selectedEnv)
                  .then((data) => setRemoteParams(data))
                  .catch(() => setRemoteParams([]))
                  .finally(() => setLoading(false))
              }}
              title="Refresh remote"
              className="h-8 w-8"
            >
              <ArrowPathIcon className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
          )}

        </div>
      </div>

      {!selectedEnv && (
        <div className="flex-1 flex flex-col items-center justify-center gap-4">
          {environments.length === 0 ? (
            <>
              <Typography variant="admin_txt1_dim" className="text-center max-w-sm">
                Register remote site environments to enable Param Sync
              </Typography>
              <a href="/admin/environments">
                <Button variant="admin_primary">Go to Environments</Button>
              </a>
            </>
          ) : (
            <>
              <Typography variant="admin_txt1_dim">Select a remote environment to continue</Typography>
              <div className="w-64">
                <DropdownSelect
                  variant="admin"
                  options={envOptions}
                  value={selectedEnvOption}
                  onChange={(sel) => {
                    const env = environments.find((e) => e.id === sel[0]?.value)
                    setSelectedEnv(env ?? null)
                    setPending({})
                    setRemoteParams([])
                  }}
                  placeholder="Select remote env"
                />
              </div>
            </>
          )}
        </div>
      )}

      {selectedEnv && (
        <>
          {/* Toolbar */}
          <div className="flex items-center gap-3 px-6 py-3 border-b border-admin_bordered bg-admin_paper shrink-0 flex-wrap">
            <Input
              variant="admin"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onClear={() => setSearch("")}
              placeholder="Search address…"
              showClearButton
              className="w-64"
            />

            <StatsBar diffRows={diffRows} pending={pending} filter={filter} setFilter={setFilter} onCommit={handleCommit} committing={committing} />
          </div>

          {/* Column headers */}
          <div className="grid grid-cols-[30%_1fr_1fr] gap-2 px-3 py-2 bg-admin_paper border-b border-admin_bordered shrink-0 text-xs text-admin_text_secondary font-medium">
            <div>Address</div>
            <div>Local</div>
            <div>{selectedEnv.name}</div>
          </div>

          {/* Rows */}
          <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-4 py-2 space-y-0.5">
            {loading ? (
              <div className="text-center py-16 text-admin_text_secondary">Loading…</div>
            ) : visibleRows.length === 0 ? (
              <div className="text-center py-16 text-admin_text_secondary">
                {search ? "No parameters match your search" : "No differences found"}
              </div>
            ) : (
              visibleRows.map((row) => (
                <DiffRow
                  key={row.address}
                  row={row}
                  pending={pending}
                  onCopyToRemote={copyToRemote}
                  onCopyToLocal={copyToLocal}
                  onEditParam={setEditingParam}
                  onViewRemote={setViewingRemote}
                />
              ))
            )}
          </div>
        </>
      )}

      {editingParam && (
        <ParamEditDialog
          param={editingParam}
          mode="edit"
          onClose={() => setEditingParam(null)}
          onRefresh={() => {
            setEditingParam(null)
            GetParams({ itemsPerPage: ALL_PARAMS_LIMIT, page: 1 }).then((res) => {
              if (res?.result) setLocalParams(res.data || [])
            })
          }}
        />
      )}

      {viewingRemote && (
        <RemoteParamView
          param={viewingRemote}
          envName={selectedEnv?.name}
          onClose={() => setViewingRemote(null)}
        />
      )}
    </div>
  )
}
